import React, { useState, useEffect, useRef } from "react";
import Whoami from "./Whoami";
import Help from "./Help";
import Dev from "./Dev";
import Vision from "./Vision";
import Benchmark from "./Benchmark";
import Probe from "./Probe";
import Hardware from "./Hardware";
import UndefinedCommand from "./UndefinedCommand";
import { useTerminalSequence } from "../hooks/useTerminalSequence";
import { useLanguage } from "../context/LanguageContext";
import "./../css/terminal.scss";

const COMMAND_REGISTRY: Record<string, React.ComponentType<any>> = {
  whoami: Whoami,
  help: Help,
  dev: Dev,
  vision: Vision,
  run: Benchmark,
  probe: Probe,
  hardware: Hardware,
};

const Terminal: React.FC = () => {
  const { lang, setLang } = useLanguage();
  const [elements, setElements] = useState<JSX.Element[]>([]);
  const [blockedPrompt, setBlockedPrompt] = useState<boolean>(false);
  const inputRef = useRef<HTMLSpanElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const PROMPT_LABEL = "jacob@dev:~$";

  const { inputText, setInputText, isTyping, shouldExecute, setShouldExecute } =
    useTerminalSequence("whoami");

  const lastCmdIdRef = useRef<number>(0);
  const animRef = useRef<number | null>(null);

  const goToBottom = () => {
    setTimeout(() => {
      if (screenRef.current) {
        screenRef.current.scrollTop = screenRef.current.scrollHeight;
      }
    }, 50);
  };

  const cancelScrollAnim = () => {
    if (animRef.current !== null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  };

  // Si el usuario interactúa con el scroll, cancelamos la subida lenta
  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return;
    const cancel = () => cancelScrollAnim();
    screen.addEventListener("wheel", cancel, { passive: true });
    screen.addEventListener("touchmove", cancel, { passive: true });
    screen.addEventListener("pointerdown", cancel);
    return () => {
      screen.removeEventListener("wheel", cancel);
      screen.removeEventListener("touchmove", cancel);
      screen.removeEventListener("pointerdown", cancel);
    };
  }, []);

  // Subida lenta y sutil desde el final hasta el inicio del último comando
  const animateScrollUp = (cmdId: number) => {
    const screen = screenRef.current;
    if (!screen) return;
    const el = document.getElementById(`cmdline-${cmdId}`);
    if (!el) return;
    const targetAbs =
      screen.scrollTop +
      el.getBoundingClientRect().top -
      screen.getBoundingClientRect().top -
      8;
    cancelScrollAnim();
    const start = screen.scrollTop;
    const end = Math.max(0, Math.min(targetAbs, screen.scrollHeight - screen.clientHeight));
    if (Math.abs(end - start) < 4) return;
    const duration = 2600;
    const t0 = performance.now();
    const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const step = (now: number) => {
      const p = Math.min((now - t0) / duration, 1);
      screen.scrollTop = start + (end - start) * ease(p);
      if (p < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
      }
    };
    animRef.current = requestAnimationFrame(step);
  };

  const scheduleSlowRead = (cmdId: number, delay = 320) => {
    setTimeout(() => {
      if (lastCmdIdRef.current === cmdId) animateScrollUp(cmdId);
    }, delay);
  };

  useEffect(() => {
    if (elements.length > 0) goToBottom();
  }, [elements]);

  useEffect(() => {
    if (shouldExecute) {
      executeCommand(inputText);
      setShouldExecute(false);
    }
  }, [shouldExecute]);

  const moveCursorToEnd = () => {
    if (!isTyping && inputRef.current) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(inputRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.textContent = inputText;
      if (!isTyping) moveCursorToEnd();
    }
  }, [inputText, isTyping]);

  const clearTerminal = () => {
    cancelScrollAnim();
    setElements([]);
    setInputText("");
    goToBottom();
  };

  const executeCommand = (commandLine: string) => {
    const [baseCommand, ...args] = commandLine.split(/\s+/);

    if (baseCommand === "clear" || baseCommand === "cls") {
      clearTerminal();
      return;
    }

    if (baseCommand === "version") {
      setElements([
        ...elements,
        <div key={Date.now()} className="console-block px-2 py-2">
          <div className="term-line">
            <span className="prompt">{PROMPT_LABEL}</span>
            <span className="text">{commandLine}</span>
          </div>
          <div className="console-response mt-2">
            SOVEREIGN_OS v5.0.0-STABLE<br />
            Build: 2026.05.06-METAL<br />
            Kernel: React-TS-Infrastructure
          </div>
        </div>,
      ]);
      setInputText("");
      goToBottom();
      return;
    }

    if (baseCommand === "lang") {
      const requested = args[0];
      const newLang =
        requested === "en" || requested === "es"
          ? requested
          : lang === "es"
          ? "en"
          : "es";
      setLang(newLang);
      setElements([
        ...elements,
        <div key={Date.now()} className="console-block px-2 py-2">
          <div className="term-line">
            <span className="prompt">{PROMPT_LABEL}</span>
            <span className="text">{commandLine}</span>
          </div>
          <div className="console-response mt-2">
            {newLang === "en" ? "Language updated to: English" : "Idioma actualizado a: Español"}
          </div>
        </div>,
      ]);
      setInputText("");
      goToBottom();
      return;
    }

    if (baseCommand === "config" && args[0] === "--lang") {
      const newLang = args[1];
      if (newLang === "en" || newLang === "es") {
        setLang(newLang);
        setElements([
          ...elements,
          <div key={Date.now()} className="console-block px-2 py-2">
            <div className="term-line">
              <span className="prompt">{PROMPT_LABEL}</span>
              <span className="text">{commandLine}</span>
            </div>
            <div className="console-response mt-2">
              {newLang === "en" ? "Language updated to: English" : "Idioma actualizado a: Español"}
            </div>
          </div>,
        ]);
        setInputText("");
        goToBottom();
        return;
      }
    }

    const CommandComponent = COMMAND_REGISTRY[baseCommand];
    const cmdId = Date.now();
    lastCmdIdRef.current = cmdId;
    setElements([
      ...elements,
      <React.Fragment key={cmdId}>
        <div className="term-line px-2 pt-2" id={`cmdline-${cmdId}`}>
          <span className="prompt">{PROMPT_LABEL}</span>
          <span className="text">{commandLine}</span>
        </div>
        <div className="console-response">
          {CommandComponent ? (
            <CommandComponent
              option={args[0]?.replace("--", "")}
              blockingAction={() => setBlockedPrompt(true)}
              unblockingAction={() => {
                setBlockedPrompt(false);
                scheduleSlowRead(cmdId, 350);
              }}
            />
          ) : (
            <UndefinedCommand command={commandLine} unblockingAction={() => setBlockedPrompt(false)} />
          )}
        </div>
      </React.Fragment>,
    ]);
    setInputText("");
    goToBottom();
    scheduleSlowRead(cmdId);
  };

  const handleInput = (event: React.FormEvent<HTMLSpanElement>) => {
    if (isTyping) return;
    setInputText(event.currentTarget.textContent || "");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (isTyping) return;

    if (event.ctrlKey && event.key.toLowerCase() === "l") {
      event.preventDefault();
      clearTerminal();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const command = inputRef.current?.textContent?.trim().toLowerCase() || "";
      if (command) executeCommand(command);
    }
  };

  const prompt = () => (
    <div className="term-line px-2 pt-2 pb-3" onClick={() => inputRef.current?.focus()}>
      <span className="prompt">{PROMPT_LABEL}</span>
      <div className="input-container">
        <span
          id="comm"
          className="input"
          contentEditable={!isTyping}
          ref={inputRef}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          style={{ outline: "none", minWidth: "10px" }}
        ></span>
        <span className="cursor"></span>
      </div>
    </div>
  );

  return (
    <>
      <button
        className="lang-corner"
        onClick={() => setLang(lang === "es" ? "en" : "es")}
        title={lang === "es" ? "Switch to English" : "Cambiar a Español"}
      >
        [{lang === "es" ? "EN" : "ES"}]
      </button>
      <div className="term-screen" ref={screenRef}>
        <div className="motd">
          <div className="motd-line">
            {lang === "es" ? "jacob@dev login — bienvenido" : "jacob@dev login — welcome"}
          </div>
          <div className="motd-line">
            {lang === "es"
              ? "Kernel 5.0.0-sovereign · React-TS · uptime 19 años"
              : "Kernel 5.0.0-sovereign · React-TS · uptime 19 years"}
          </div>
          <div className="motd-line">
            {lang === "es"
              ? "locale: es-MX · shell: zsh"
              : "locale: en-US · shell: zsh"}
          </div>
          <div className="motd-line motd-hint">
            {lang === "es"
              ? "escribe help para ver los comandos · lang cambia el idioma · ctrl+l limpia"
              : "type help to list commands · lang switches language · ctrl+l clears"}
          </div>
        </div>

        {elements}
        {!blockedPrompt && prompt()}
        <div id="terminal-bottom" />
      </div>
    </>
  );
};

export default Terminal;
